import type { Message } from "./types";

export type SquareTeamSystemEvent = {
  action: "joined" | "left";
  people: string;
};

export type TeamFile = Message["attachments"][number] & {
  createdAt: number;
  folder: string;
  messageId: string;
};

const teamFilesBatch = /^Square Team Files archive\s+—\s+(.+?)\s+—\s+batch\s+\d+\/\d+/iu;
const squareSystemEvent = /^Square Team system event:\s*([A-Z_]+)\s+—\s+([\s\S]+)$/u;

export function teamFileFolder(messageText: string): string {
  const match = teamFilesBatch.exec(messageText.trim());
  if (match === null) return "";
  const sourceFolder = match[1]?.trim() ?? "";
  return sourceFolder.toLocaleLowerCase() === "root files" ? "" : sourceFolder;
}

export function buildTeamFileLibrary(messages: readonly Message[]): TeamFile[] {
  return messages.flatMap((message) => {
    const folder = teamFileFolder(message.content.plainText);
    return message.attachments.map((attachment) => ({
      ...attachment,
      createdAt: message.createdAt,
      folder,
      messageId: message.id
    }));
  });
}

export function parseSquareTeamSystemEvent(messageText: string): SquareTeamSystemEvent | null {
  // Some events carry a trailing "[Updated in Square]" line, so the archive
  // markers come off before the event itself is read.
  const match = squareSystemEvent.exec(parseMigratedSquareMessage(messageText).text);
  if (match === null) return null;
  const normalized = (match[1] ?? "").replaceAll("_", "");
  const people = (match[2] ?? "").trim();
  if (!people) return null;
  if (normalized === "PEOPLEJOINEDCONVERSATION") return { action: "joined", people };
  if (normalized === "PEOPLEREMOVEDFROMCONVERSATION") return { action: "left", people };
  return null;
}

// The Square export writes its own bookkeeping into the message body: the
// reactions a message collected, whether it was edited, and a stand-in line
// for any picture or clip. Those lines are archive metadata, not something a
// person typed, so they are lifted out of the text and shown as real UI.
export type MigratedSquareMessage = {
  text: string;
  reactions: readonly { emoji: string; count: number }[];
  editedInSquare: boolean;
  deletedInSquare: boolean;
  mediaUnavailable: boolean;
};

const squareEmoji: Readonly<Record<string, string>> = {
  bulb: "💡", clap: "👏", clinking_glasses: "🥂", cry: "😢", exploding_head: "🤯",
  heart: "❤️", joy: "😂", ok_hand: "👌", pray: "🙏", raised_hand: "✋",
  raised_hands: "🙌", tada: "🎉", thumbsup: "👍", triangular_flag_on_post: "🚩"
};

const squareMarker = /\[(Updated in Square|Square Team (?:image|video)|Deleted Square Team message|Reply imported from Square|Square reactions:[^\]\n]*|Square (?:image|video) unavailable:[^\]\n]*)\]/gu;
const reactionPart = /:([a-z0-9_+-]+):\s*x(\d+)/giu;

export function parseMigratedSquareMessage(messageText: string): MigratedSquareMessage {
  const reactions: { emoji: string; count: number }[] = [];
  let editedInSquare = false;
  let deletedInSquare = false;
  let mediaUnavailable = false;

  const text = messageText.replace(squareMarker, (_full, marker: string) => {
    if (marker === "Updated in Square") editedInSquare = true;
    else if (marker === "Deleted Square Team message") deletedInSquare = true;
    else if (marker.startsWith("Square image unavailable:") || marker.startsWith("Square video unavailable:")) mediaUnavailable = true;
    else if (marker.startsWith("Square reactions:")) {
      for (const part of marker.matchAll(reactionPart)) {
        const name = (part[1] ?? "").toLocaleLowerCase();
        const count = Number.parseInt(part[2] ?? "0", 10);
        if (count > 0) reactions.push({ emoji: squareEmoji[name] ?? `:${name}:`, count });
      }
    }
    return "";
  }).replace(/\n{3,}/gu, "\n\n").trim();

  return { text, reactions, editedInSquare, deletedInSquare, mediaUnavailable };
}
